<template>
  <div class="drug-search" ref="root">
    <input type="text"
           ref="input"
           role="combobox"
           aria-autocomplete="list"
           :aria-expanded="isOpen"
           :aria-activedescendant="activeId"
           data-kt-user-table-filter="search"
           :value="modelValue"
           @input="updateValue"
           @keydown.down.prevent="move(1)"
           @keydown.up.prevent="move(-1)"
           @keydown.enter="onEnter"
           @keydown.esc="close"
           @focus="reopen"
           v-bind="$attrs"
           class="form-control form-control-solid"
           placeholder="Pesquisar o medicamento..."
           autocomplete="off"/>

    <Teleport to="body">
      <div v-if="isOpen" ref="panel" class="drug-search-panel" :style="panelStyle">
        <ul v-if="drugs.length" class="drug-search-list" role="listbox">
          <li v-for="(drug, index) in drugs" :key="drug.id_drug ?? index"
              :id="optionId(index)"
              role="option"
              :aria-selected="index === activeIndex"
              :class="['drug-search-option', { 'is-active': index === activeIndex }]"
              @mouseenter="activeIndex = index"
              @mousedown.prevent="setSearchDrug(drug)">
            <span class="drug-search-name">{{ drugLabel(drug) }}</span>
            <span class="drug-search-meta">
              <small v-if="drug.substance" class="drug-search-substance">{{ drug.substance.substance_name }}</small>
              <RegulatoryBadge :classification="badgeFor(drug)"/>
            </span>
          </li>
        </ul>

        <p v-else-if="loading" class="drug-search-state">Buscando...</p>
        <p v-else class="drug-search-state">
          Nenhum medicamento encontrado. Você pode digitar o nome livremente —
          o receituário terá de ser escolhido antes de gerar.
        </p>
      </div>
    </Teleport>
  </div>
</template>

<script>
import http from "../helpers/http";
import RegulatoryBadge from "@/views/attendance/shared/RegulatoryBadge.vue";
import {loadRegulatoryTypes} from "@/helpers/regulatoryTypes";

const MINIMO = 3;
const ESPERA = 250;

export default {
  name: "InputSearchDrug",
  components: {RegulatoryBadge},
  inheritAttrs: false,
  emits: ["onSetSearchDrug", "onClearSearchDrug", "update:modelValue"],
  props: {
    modelValue: ''
  },
  data() {
    return {
      drugs: [],
      isOpen: false,
      loading: false,
      activeIndex: -1,
      timer: null,
      panelStyle: {},
      // Resposta lenta de uma busca antiga nao pode sobrescrever a atual.
      sequencia: 0,
    }
  },
  computed: {
    activeId() {
      return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
    },
  },
  mounted() {
    loadRegulatoryTypes().catch(() => {});
    document.addEventListener('mousedown', this.onDocumentClick);
    // Captura para pegar tambem a rolagem do proprio corpo do modal.
    window.addEventListener('scroll', this.updatePosition, true);
    window.addEventListener('resize', this.updatePosition);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocumentClick);
    window.removeEventListener('scroll', this.updatePosition, true);
    window.removeEventListener('resize', this.updatePosition);
    clearTimeout(this.timer);
  },
  methods: {
    focus() {
      this.$refs.input?.focus();
    },
    optionId(index) {
      return `drug-search-option-${this.$.uid}-${index}`;
    },
    updateValue(event) {
      this.$emit('update:modelValue', event.target.value);
      // Digitar por cima desfaz a escolha do catalogo. Sem isto, corrigir o
      // nome para um manipulado mantem o id_drug do medicamento industrializado
      // e a API deriva o receituario do medicamento errado.
      this.$emit('onClearSearchDrug');
      this.scheduleSearch(event.target.value);
    },
    scheduleSearch(term) {
      clearTimeout(this.timer);

      if ((term ?? '').trim().length < MINIMO) {
        this.drugs = [];
        this.close();

        return;
      }

      this.loading = true;
      this.open();
      this.timer = setTimeout(() => this.getDrugs(term), ESPERA);
    },
    getDrugs(term) {
      const atual = ++this.sequencia;

      http.get(`/drugs`, { params: { search: term } }).then((response) => {
        if (atual !== this.sequencia) {
          return;
        }

        this.drugs = response.data ?? [];
        this.activeIndex = this.drugs.length ? 0 : -1;
        this.loading = false;
      }).catch(() => {
        if (atual !== this.sequencia) {
          return;
        }

        this.drugs = [];
        this.loading = false;
      });
    },
    move(passo) {
      if (!this.isOpen || !this.drugs.length) {
        return;
      }

      const total = this.drugs.length;
      this.activeIndex = (this.activeIndex + passo + total) % total;
      this.scrollToActive();
    },
    scrollToActive() {
      this.$nextTick(() => {
        this.$refs.panel
            ?.querySelector('.drug-search-option.is-active')
            ?.scrollIntoView({block: 'nearest'});
      });
    },
    /**
     * O projeto tem `.modal-body { overflow-y: auto }`, entao um painel
     * posicionado dentro do modal era recortado. Ele vive no body e se ancora
     * ao campo pelo rect — e vira para cima quando nao ha espaco embaixo.
     */
    updatePosition() {
      const campo = this.$refs.input;

      if (!this.isOpen || !campo) {
        return;
      }

      const rect = campo.getBoundingClientRect();
      const abaixo = window.innerHeight - rect.bottom;
      const viraParaCima = abaixo < 220 && rect.top > abaixo;

      this.panelStyle = viraParaCima
          ? {
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            bottom: `${window.innerHeight - rect.top + 4}px`,
            maxHeight: `${rect.top - 16}px`,
          }
          : {
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            top: `${rect.bottom + 4}px`,
            maxHeight: `${abaixo - 16}px`,
          };
    },
    onEnter(event) {
      if (!this.isOpen || this.activeIndex < 0) {
        return;
      }

      // Só engole o Enter quando há escolha a fazer: fora disso o formulário
      // continua submetendo normalmente.
      event.preventDefault();
      this.setSearchDrug(this.drugs[this.activeIndex]);
    },
    reopen() {
      if (this.drugs.length) {
        this.open();
      }
    },
    open() {
      this.isOpen = true;
      this.$nextTick(this.updatePosition);
    },
    close() {
      this.isOpen = false;
      this.activeIndex = -1;
    },
    onDocumentClick(event) {
      const dentro = this.$refs.root?.contains(event.target)
          || this.$refs.panel?.contains(event.target);

      if (!dentro) {
        this.close();
      }
    },
    /**
     * O selo vem do tipo que a API derivou para aquele medicamento do
     * catalogo. O medico ve a consequencia antes de escolher, nao depois.
     */
    badgeFor(drug) {
      return {
        status: drug.regulatory_status ?? 'CLASSIFIED',
        regulatory_type: drug.regulatory_type ?? null,
        regulatory_type_description: drug.regulatory_type_description ?? null,
        reason_description: 'Sem classificação: o receituário terá de ser escolhido antes de gerar.',
        manual: false,
      };
    },
    drugLabel(drug) {
      return [drug.drug_name, drug.concentration || drug.presentation, drug.pharmaceutical_form]
          .filter(Boolean)
          .join(' - ');
    },
    setSearchDrug(drug) {
      this.$emit('update:modelValue', drug.drug_name);
      this.close();
      this.$emit('onSetSearchDrug', {
        id_drug: drug.id_drug ?? null,
        drug_name: drug.drug_name,
        concentration: drug.concentration ?? null,
        pharmaceutical_form: drug.pharmaceutical_form ?? null,
        presentation: drug.presentation ?? null,
        substance_name: drug.substance?.substance_name ?? null,
        regulatory_status: drug.regulatory_status ?? null,
        regulatory_type: drug.regulatory_type ?? null,
        regulatory_type_description: drug.regulatory_type_description ?? null,
      });
    },
  }
}
</script>

<style scoped>
.drug-search {
  position: relative;
}

/* Flutua sobre o formulário: inline, a lista empurrava os campos de baixo a
   cada tecla digitada. */
.drug-search-panel {
  position: fixed;
  z-index: 2300;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.drug-search-list {
  list-style: none;
  margin: 0;
  padding: 4px;
  overflow-y: auto;
}

.drug-search-option {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
}

.drug-search-option.is-active {
  background: #f1f3f9;
}

.drug-search-name {
  color: #181c32;
  line-height: 1.3;
}

.drug-search-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.drug-search-substance {
  color: #a1a5b7;
  font-size: 0.8rem;
}

.drug-search-state {
  margin: 0;
  padding: 12px 14px;
  color: #7e8299;
  font-size: 0.875rem;
  line-height: 1.45;
}
</style>
